import { useEffect, useMemo, useState } from 'react';
import { Country, State, City } from 'country-state-city';
import { directoryApi } from '../services/api';

const COUNTRY_NAME_ALIASES = {
  'Antigua & Barbuda': 'Antigua and Barbuda',
  'Bahamas': 'Bahamas',
  'British Virgin Islands': 'Virgin Islands, British',
  'Cabo Verde': 'Cape Verde',
  'Cayman Islands': 'Cayman Islands',
  'Cote d’Ivoire': "Côte d'Ivoire",
  "Cote d'Ivoire": "Côte d'Ivoire",
  'Democratic Republic of the Congo': 'Congo, The Democratic Republic of the',
  'Gambia': 'Gambia',
  'Hong Kong': 'Hong Kong',
  'Iran': 'Iran, Islamic Republic of',
  'Laos': "Lao People's Democratic Republic",
  'Macau': 'Macao',
  'Micronesia': 'Micronesia, Federated States of',
  'Moldova': 'Moldova, Republic of',
  'Myanmar': 'Myanmar',
  'North Korea': "Korea, Democratic People's Republic of",
  'Palestine': 'Palestine',
  'Russia': 'Russian Federation',
  'South Korea': 'Korea, Republic of',
  'Syria': 'Syrian Arab Republic',
  'Taiwan': 'Taiwan',
  'Tanzania': 'Tanzania, United Republic of',
  'Turkey': 'Turkey',
  'United Kingdom': 'United Kingdom',
  'United States': 'United States',
  'Virgin Islands, U.S.': 'Virgin Islands, U.S.',
};

function normalizeCountryKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[’‘`]/g, "'")
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const FALLBACK_COUNTRY_NAMES = Array.from(
  new Set(Country.getAllCountries().map((country) => country.name))
).sort((a, b) => a.localeCompare(b));

function findCountryRecordByName(countryName) {
  const raw = String(countryName || '').trim();
  if (!raw) return null;

  const candidates = [
    raw,
    COUNTRY_NAME_ALIASES[raw],
  ].filter(Boolean);

  for (const candidate of candidates) {
    const exact = Country.getAllCountries().find((country) => country.name === candidate);
    if (exact) return exact;
  }

  const normalized = normalizeCountryKey(raw);
  return Country.getAllCountries().find((country) => {
    const candidateNames = [
      country.name,
      COUNTRY_NAME_ALIASES[country.name],
    ].filter(Boolean);
    return candidateNames.some((candidate) => normalizeCountryKey(candidate) === normalized);
  }) || null;
}

export function getCountryIsoCode(countryName) {
  return findCountryRecordByName(countryName)?.isoCode || null;
}

export function getPhoneCodeForCountry(countryName) {
  const phoneCode = findCountryRecordByName(countryName)?.phonecode;
  if (!phoneCode) return '+1';
  const part = String(phoneCode).split('-')[0].trim();
  return part.startsWith('+') ? part : `+${part}`;
}

export function getStateOptionsForCountry(countryName) {
  const countryIsoCode = getCountryIsoCode(countryName);
  if (!countryIsoCode) return [];
  return State.getStatesOfCountry(countryIsoCode).map((state) => ({
    value: state.name,
    label: state.name,
  }));
}

export function getStateIsoCode(countryName, stateName) {
  const countryIsoCode = getCountryIsoCode(countryName);
  if (!countryIsoCode || !stateName) return null;
  const state = State.getStatesOfCountry(countryIsoCode).find((item) => item.name === stateName);
  return state?.isoCode || null;
}

export function getCityOptionsForCountry(countryName, stateName = null) {
  const countryIsoCode = getCountryIsoCode(countryName);
  if (!countryIsoCode) return [];

  if (stateName) {
    const stateIsoCode = getStateIsoCode(countryName, stateName);
    if (stateIsoCode) {
      return City.getCitiesOfState(countryIsoCode, stateIsoCode).map((city) => ({
        value: city.name,
        label: city.name,
      }));
    }
  }

  return City.getCitiesOfCountry(countryIsoCode).map((city) => ({
    value: city.name,
    label: city.name,
  }));
}

export function useCanonicalCountryOptions(includeAllOption = false) {
  const [countryNames, setCountryNames] = useState(FALLBACK_COUNTRY_NAMES);

  useEffect(() => {
    let ignore = false;

    directoryApi.getCountries()
      .then((response) => {
        if (ignore) return;
        const names = Array.isArray(response.data) && response.data.length > 0
          ? response.data
          : FALLBACK_COUNTRY_NAMES;
        setCountryNames(names);
      })
      .catch(() => {
        if (!ignore) setCountryNames(FALLBACK_COUNTRY_NAMES);
      });

    return () => {
      ignore = true;
    };
  }, []);

  return useMemo(() => {
    const options = countryNames.map((country) => ({ value: country, label: country }));
    return includeAllOption
      ? [{ value: 'all', label: 'All Countries' }, ...options]
      : options;
  }, [countryNames, includeAllOption]);
}
